import * as React from 'react'
import { Loader2 } from 'lucide-react'
import { toast } from 'sonner'
import {
  getOntologyBranchHeaderValue,
  useDeleteBranchMutation,
  useSwitchBranch,
  type Branch
} from '@axilon-apps/domains-ontology-ui-state'
import { Button } from '../ui/button'
import { Input } from '../ui/input'
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from '../ui/alert-dialog'

export interface BranchDeleteDialogProps {
  branch: Branch
  open: boolean
  onOpenChange: (open: boolean) => void
}

/**
 * Type-to-confirm delete dialog for a branch. Soft-deletes the branch and
 * drops its working database (uncommitted edits are lost; committed history
 * remains); switches the active branch back to main on success when the
 * deleted branch was the active one.
 */
export function BranchDeleteDialog({ branch, open, onOpenChange }: BranchDeleteDialogProps) {
  const [deleteBranch, deleteState] = useDeleteBranchMutation()
  const { switchBranch } = useSwitchBranch()
  const [confirm, setConfirm] = React.useState('')
  const [submitError, setSubmitError] = React.useState('')

  React.useEffect(() => {
    if (!open) {
      setConfirm('')
      setSubmitError('')
      deleteState.reset()
    }
  }, [open, deleteState])

  const submitting = deleteState.isLoading
  const matches = confirm === branch.name

  const handleDelete = async () => {
    if (!matches || submitting) return
    setSubmitError('')
    try {
      await deleteBranch(branch.id).unwrap()
      toast.success(`Deleted ${branch.name}`)
      if (getOntologyBranchHeaderValue() === branch.id) {
        // Switching to main never opens anything, so this cannot fail —
        // don't leave the header pointing at a dropped database.
        void switchBranch(undefined)
      }
      onOpenChange(false)
    } catch (err) {
      setSubmitError(extractDeleteError(err))
    }
  }

  return (
    <AlertDialog
      open={open}
      onOpenChange={(next) => {
        if (submitting) return
        onOpenChange(next)
      }}
    >
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Delete {branch.name}?</AlertDialogTitle>
          <AlertDialogDescription>
            This deletes the branch and all unmerged changes. Type{' '}
            <span className="font-mono text-ink">{branch.name}</span> to confirm.
          </AlertDialogDescription>
        </AlertDialogHeader>

        <Input
          value={confirm}
          onChange={(event) => setConfirm(event.target.value)}
          placeholder={branch.name}
          aria-label="Branch name confirmation"
          autoComplete="off"
          autoFocus
          className="font-mono"
          disabled={submitting}
        />

        {submitError ? (
          <p role="alert" className="text-sm text-st-down">
            {submitError}
          </p>
        ) : null}

        <AlertDialogFooter>
          <AlertDialogCancel disabled={submitting}>Cancel</AlertDialogCancel>
          <Button variant="destructive" onClick={handleDelete} disabled={!matches || submitting}>
            {submitting ? <Loader2 className="size-4 animate-spin" /> : null}
            Delete branch
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}

function extractDeleteError(err: unknown): string {
  if (err && typeof err === 'object' && 'data' in err) {
    const data = (err as { data?: unknown }).data
    if (data && typeof data === 'object' && 'message' in data) {
      const message = (data as { message?: unknown }).message
      if (typeof message === 'string' && message.length > 0) return message
    }
  }
  return 'Could not delete branch.'
}
